// @/plugins/inactivity.ts
// Inactivity auto-logout plugin with cross-tab synchronization
// - Tracks user activity events and updates a shared timestamp in localStorage
// - Periodically checks for inactivity and triggers a single cross-tab logout
// - Listens to storage events to keep tabs in sync

import { useAuthStore } from '@/stores';

type InactivityOptions = {
    // Inactivity timeout in milliseconds (required)
    timeoutMs: number;
};

const DEFAULT_CHECK_INTERVAL = 30_000; // 30s
const DEFAULT_PREFIX = 'idle';

// localStorage keys (namespaced by prefix)
const makeKeys = (prefix: string) => ({
    lastActivity: `${prefix}.lastActivity`,
    logoutSignal: `${prefix}.logout`,
});

// Unique tab id for coordination
const TAB_ID = `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;

class InactivityManager {
    private options: Required<InactivityOptions>;
    private intervalId: number | undefined;
    private keys: ReturnType<typeof makeKeys>;
    private isLoggedOut = false;
    private started = false;
    // Listener references to properly detach
    private onVisibilityChangeRef: (() => void) | undefined;
    private onStorageRef: ((ev: StorageEvent) => void) | undefined;

    constructor() {
        this.options = {
            timeoutMs: 0,
        } as Required<InactivityOptions>;
        this.keys = makeKeys(DEFAULT_PREFIX);
    }

    private init() {
        // Initialize the lastActivity with now if missing
        if (!localStorage.getItem(this.keys.lastActivity)) {
            this.updateLastActivity();
        }

        this.attachListeners();

        // Periodic check
        this.intervalId = window.setInterval(() => this.checkInactivity(), DEFAULT_CHECK_INTERVAL);

        // storage sync is in attachListeners
    }

    private attachListeners() {
        // Activity listeners
        window.addEventListener('mousemove', this.updateLastActivity, { passive: true });
        window.addEventListener('mousedown', this.updateLastActivity, { passive: true });
        window.addEventListener('keydown', this.updateLastActivity);
        window.addEventListener('scroll', this.updateLastActivity, { passive: true });
        window.addEventListener('touchstart', this.updateLastActivity, { passive: true });

        // Visibility
        this.onVisibilityChangeRef = () => {
            if (document.visibilityState === 'visible') this.updateLastActivity();
        };
        document.addEventListener('visibilitychange', this.onVisibilityChangeRef);

        // Storage
        this.onStorageRef = (ev: StorageEvent) => this.onStorage(ev);
        window.addEventListener('storage', this.onStorageRef);
    }

    private detachListeners() {
        window.removeEventListener('mousemove', this.updateLastActivity);
        window.removeEventListener('mousedown', this.updateLastActivity);
        window.removeEventListener('keydown', this.updateLastActivity);
        window.removeEventListener('scroll', this.updateLastActivity);
        window.removeEventListener('touchstart', this.updateLastActivity);
        if (this.onVisibilityChangeRef) {
            document.removeEventListener('visibilitychange', this.onVisibilityChangeRef);
            this.onVisibilityChangeRef = undefined;
        }
        if (this.onStorageRef) {
            window.removeEventListener('storage', this.onStorageRef);
            this.onStorageRef = undefined;
        }
    }

    start(opts: InactivityOptions) {
        if (this.started) this.stop();

        // Do not start if timeout is invalid or disabled
        if (!opts.timeoutMs || opts.timeoutMs <= 0) return;

        this.options = opts;
        this.keys = makeKeys(DEFAULT_PREFIX);
        this.isLoggedOut = false;
        this.started = true;
        /* eslint-disable @typescript-eslint/no-explicit-any */
        (window as any).__InactivityMgr = this; // optional debug hook
        this.init();
    }

    stop() {
        this.started = false;
        this.isLoggedOut = false;
        if (this.intervalId) {
            window.clearInterval(this.intervalId);
            this.intervalId = undefined;
        }
        this.detachListeners();
    }

    private getNow(): number {
        return Date.now();
    }

    private updateLastActivity = () => {
        if (this.isLoggedOut || !this.started) return;
        // Only track activity when the user is authenticated
        if (!localStorage.getItem('token')) return;
        try {
            localStorage.setItem(this.keys.lastActivity, String(this.getNow()));
        } catch (_) {
            // ignore
        }
    };

    private onStorage(ev: StorageEvent) {
        if (!ev.key) return;
        if (ev.key === this.keys.logoutSignal && ev.newValue) {
            // Another tab initiated logout
            this.triggerLogout('signal');
        }
        // lastActivity updates are passively read on checkInactivity()
    }

    private checkInactivity() {
        if (this.isLoggedOut || !this.started) return;

        // Skip checks if the user is not authenticated
        if (!localStorage.getItem('token')) return;

        const raw = localStorage.getItem(this.keys.lastActivity);
        const last = raw ? Number(raw) : 0;
        const idleFor = this.getNow() - last;
        if (idleFor >= this.options.timeoutMs) {
            // Attempt to be the coordinator: set a logout signal with payload
            try {
                const payload = JSON.stringify({ at: this.getNow(), by: TAB_ID });
                localStorage.setItem(this.keys.logoutSignal, payload);
            } catch (_) {
                // ignore
            }
            // And proceed to log out action
            this.triggerLogout('timeout');
        }
    }

    private triggerLogout(reason: 'timeout' | 'signal') {
        if (this.isLoggedOut) return;
        this.isLoggedOut = true;
        // Clear timers/listeners
        if (this.intervalId) {
            window.clearInterval(this.intervalId);
        }

        useAuthStore().logout(reason);
    }
}

// Export a singleton instance to be controlled by the auth flow
export const inactivity = new InactivityManager();

export default inactivity;
